import { authFromCallback, tenantFromCallback, type AuthProvider, type TenantProvider } from '@vextura/sdk-runtime';
import { type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { useScope } from '../scope/ScopeProvider.js';

/**
 * Bridges the shell's auth + scope contexts into the SDK runtime's
 * AuthProvider / TenantProvider interfaces. Live refs are updated by
 * <SdkBindings /> on every shell re-render, so the next SDK call always
 * sees fresh values without per-request React-context lookups.
 *
 * The SDK never imports React — these refs are the *only* React-aware
 * surface. Generated clients (@vextura/sdk-vex-iam etc.) receive plain
 * objects.
 *
 * Lives in @vextura/shell so every service section (service-iam,
 * service-auth, service-vault, …) can construct its own typed client
 * without re-importing from apps/console.
 */

const authTokenRef: { current: string | undefined } = { current: undefined };
const tenantRef: { current: string | undefined } = { current: undefined };

export const shellAuthProvider: AuthProvider = authFromCallback(() => authTokenRef.current);
export const shellTenantProvider: TenantProvider = tenantFromCallback(() => tenantRef.current);

/**
 * Mount this once inside the AuthProvider + ScopeProvider subtree. It
 * keeps the SDK-facing refs in sync with the live shell context.
 */
export function SdkBindings({ children }: { children: ReactNode }): ReactNode {
  const { token } = useAuth();
  const { tenant } = useScope();
  authTokenRef.current = token ?? undefined;
  tenantRef.current = tenant ?? undefined;
  return <>{children}</>;
}
